<template>
    <article
        :class="[
            'foxy-card p-6 overflow-hidden',
            item.placement === 'pinned' ? 'border-foxy-warning bg-foxy-honey/15' : ''
        ]"
        :data-feed-key="item.key"
        :data-feed-family="item.family"
    >
        <div v-if="item.placement === 'pinned'" class="mb-4 pb-3 border-b border-foxy-warning/35">
            <span class="inline-flex items-center gap-1.5 bg-foxy-warning text-white px-3 py-1 rounded-full text-xs font-bold">
                <i class="fas fa-thumbtack" aria-hidden="true"></i>
                Закріплено
            </span>
        </div>

        <header class="mb-4 flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
            <div class="flex w-full min-w-0 items-center gap-3 sm:w-auto" data-feed-header-identity>
                <a v-if="owner && owner.username" :href="profileUrl(owner.username)"
                    class="shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20">
                    <user-avatar
                        :src="owner.avatar_display || ''"
                        :name="owner.name"
                        size="md"
                        class="bg-foxy-elevated font-semibold text-foxy-text"
                    />
                </a>
                <user-avatar
                    v-else
                    src=""
                    :name="ownerName"
                    size="md"
                    class="shrink-0 bg-foxy-elevated font-semibold text-foxy-text"
                />
                <div class="min-w-0">
                    <a v-if="owner && owner.username" :href="profileUrl(owner.username)"
                        class="block truncate font-semibold text-foxy-text hover:text-foxy-action">
                        {{ ownerName }}
                    </a>
                    <span v-else class="block truncate font-semibold text-foxy-text">{{ ownerName }}</span>
                    <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <time v-if="displayDate" :datetime="item.activity_at"
                            class="whitespace-nowrap text-sm text-foxy-text-secondary">
                            {{ displayDate }}
                        </time>
                        <span v-if="item.family === 'review' && payload.rating !== null && payload.rating !== undefined"
                            class="inline-flex items-center gap-1 text-sm font-semibold text-foxy-text"
                            data-review-rating>
                            <i class="fas fa-star text-foxy-honey" aria-hidden="true"></i>
                            {{ payload.rating }}/10
                        </span>
                    </div>
                </div>
            </div>
            <div class="flex w-full flex-wrap items-center justify-start gap-2 sm:w-auto sm:max-w-[60%] sm:shrink-0 sm:justify-end"
                data-feed-header-metadata>
                <span v-if="item.family === 'discussion'"
                    :class="[
                        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold',
                        payload.is_closed
                            ? 'border-foxy-border bg-foxy-elevated text-foxy-text-secondary'
                            : 'border-foxy-success/55 bg-foxy-success/15 text-foxy-text'
                    ]"
                    :data-discussion-state="payload.is_closed ? 'closed' : 'open'"
                    data-discussion-status>
                    <span :class="['h-1.5 w-1.5 rounded-full', payload.is_closed ? 'bg-foxy-text-secondary/60' : 'bg-foxy-success']"
                        aria-hidden="true"></span>
                    {{ payload.is_closed ? 'Закрита' : 'Відкрита' }}
                </span>
                <span v-if="item.family === 'review' && moodPresentation"
                    :class="[
                        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold',
                        moodPresentation.classes,
                    ]"
                    :data-review-mood="payload.mood">
                    <span :class="['h-2 w-2 rounded-full', moodPresentation.dotClass]" aria-hidden="true"></span>
                    <span>{{ moodPresentation.label }}</span>
                </span>
                <span class="shrink-0 rounded-full bg-foxy-elevated px-3 py-1 text-xs font-semibold text-foxy-text-secondary"
                    data-family-badge>
                    {{ familyLabel }}
                </span>
            </div>
        </header>

        <section v-if="item.family === 'discussion'">
            <a v-if="item.url" :href="item.url" data-feed-detail
                class="block text-xl font-bold text-foxy-text hover:text-foxy-action mb-3 break-words">
                {{ payload.title }}
            </a>
            <h2 v-else class="text-xl font-bold text-foxy-text mb-3 break-words">{{ payload.title }}</h2>
            <div class="relative" data-discussion-body-container>
                <p ref="discussionOverflowProbe"
                    class="feed-review-body-clamp feed-review-body-probe break-words text-foxy-text"
                    aria-hidden="true">{{ bodyPlainText }}</p>
                <div :id="discussionBodyRegionId" data-discussion-body>
                    <div v-if="showRichDiscussionBody"
                        class="content-preview break-words text-foxy-text"
                        data-discussion-body-rich
                        v-html="payload.body_html"></div>
                    <div v-else data-discussion-body-compact>
                        <div v-if="discussionFirstImage" class="content-preview" data-discussion-first-image>
                            <img :src="discussionFirstImage.src" :alt="discussionFirstImage.alt" loading="lazy">
                        </div>
                        <p v-if="bodyPlainText" class="feed-review-body-clamp break-words text-foxy-text"
                            data-discussion-text-preview>{{ bodyPlainText }}</p>
                    </div>
                </div>
                <button v-if="discussionMeasured && discussionNeedsCompact" type="button"
                    class="mt-2 inline-flex rounded-md text-sm font-semibold text-foxy-action hover:text-foxy-action/80 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                    :aria-expanded="discussionExpanded ? 'true' : 'false'"
                    :aria-controls="discussionBodyRegionId"
                    data-discussion-body-toggle
                    @click="toggleDiscussionExpanded">
                    {{ discussionExpanded ? 'Згорнути' : 'Розгорнути' }}
                </button>
            </div>
            <p class="mt-4 text-sm text-foxy-text-secondary" data-discussion-reply-count>
                {{ formatCount(payload.replies_count || 0, 'відповідь', 'відповіді', 'відповідей') }}
            </p>
            <reply-preview v-if="payload.reply_preview" :preview="payload.reply_preview" label="Відповідь" />
        </section>

        <section v-else-if="item.family === 'review'">
            <section v-if="works.length" class="mb-4" aria-label="Книга рецензії" data-review-book-context>
                <div class="grid grid-cols-1 gap-3">
                    <a v-for="work in works" :key="work.id" :href="work.url"
                        class="group flex min-w-0 items-center gap-3 rounded-lg border border-foxy-border/60 bg-foxy-elevated/50 px-3 py-2.5 transition-colors hover:border-foxy-action/40 hover:bg-foxy-elevated/65 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                        data-review-book-link>
                        <img :src="work.cover" :alt="work.title" loading="lazy"
                            class="h-20 w-14 shrink-0 rounded-lg bg-foxy-surface object-cover">
                        <span class="min-w-0">
                            <span class="block line-clamp-2 text-base font-bold text-foxy-text transition-colors group-hover:text-foxy-action">
                                {{ work.title }}
                            </span>
                            <span v-if="work.author" class="mt-1 block truncate text-sm text-foxy-text-secondary">
                                Автор · {{ work.author }}
                            </span>
                        </span>
                    </a>
                </div>
            </section>
            <div v-if="spoilerProtected"
                class="mb-4 rounded-r-lg border-l-2 border-foxy-honey/45 bg-foxy-elevated/30 px-4 py-3"
                data-review-spoiler-notice>
                <div class="relative">
                    <div class="feed-review-spoiler-preview pointer-events-none select-none"
                        aria-hidden="true"
                        data-review-spoiler-preview>
                        <p class="feed-review-body-clamp feed-review-spoiler-preview-text break-words text-foxy-text"
                            data-review-spoiler-preview-text>{{ bodyPlainText }}</p>
                    </div>
                    <div class="absolute inset-0 z-10 flex items-center justify-center px-3"
                        data-review-spoiler-gate>
                        <div class="inline-flex max-w-full items-center gap-3 rounded-lg border border-foxy-border/70 bg-foxy-surface/95 px-4 py-3 shadow-sm">
                            <span class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foxy-honey/15 text-foxy-text-secondary"
                                aria-hidden="true">
                                <i class="fas fa-eye-slash"></i>
                            </span>
                            <div class="min-w-0">
                                <p class="text-sm font-medium text-foxy-text">Цей допис містить спойлер</p>
                                <button type="button"
                                    class="mt-1.5 inline-flex items-center gap-1.5 rounded-md text-sm font-semibold text-foxy-action hover:text-foxy-action/80 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                                    :aria-expanded="spoilerRevealed ? 'true' : 'false'"
                                    :aria-controls="'feed-review-body-' + item.source_id"
                                    data-feed-spoiler-reveal
                                    @click="revealSpoiler">
                                    <i class="fas fa-eye" aria-hidden="true"></i>
                                    Показати спойлер
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!spoilerProtected" class="relative" data-review-body-container>
                <p ref="bodyOverflowProbe"
                    class="feed-review-body-clamp feed-review-body-probe break-words text-foxy-text"
                    aria-hidden="true">{{ bodyPlainText }}</p>
                <div :id="'feed-review-body-' + item.source_id" data-review-body>
                    <div v-if="showRichReviewBody"
                        class="content-preview break-words text-foxy-text"
                        data-review-body-rich
                        v-html="payload.body_html"></div>
                    <p v-else
                        class="feed-review-body-clamp break-words text-foxy-text"
                        data-review-body-compact>{{ bodyPlainText }}</p>
                </div>
                <button v-if="bodyOverflowing" type="button"
                    class="mt-2 inline-flex rounded-md text-sm font-semibold text-foxy-action hover:text-foxy-action/80 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                    :aria-expanded="bodyExpanded ? 'true' : 'false'"
                    :aria-controls="bodyRegionId"
                    data-review-body-toggle
                    @click="toggleBodyExpanded">
                    {{ bodyExpanded ? 'Згорнути' : 'Розгорнути' }}
                </button>
            </div>
            <p class="mt-3 text-sm text-foxy-text-secondary">{{ formatCount(payload.replies_count || 0, 'коментар', 'коментарі', 'коментарів') }}</p>
            <reply-preview v-if="payload.top_comment" :preview="payload.top_comment" label="Коментар" />
        </section>

        <section v-else-if="item.family === 'quote'">
            <blockquote class="feed-quote-body my-2 border-l-2 border-foxy-action pl-5 text-lg font-normal italic leading-relaxed text-foxy-text break-words whitespace-pre-line sm:pl-6 sm:text-xl"
                data-quote-body>
                “{{ payload.text }}”
            </blockquote>
            <p v-if="payload.page_number" class="mt-3 pl-5 text-xs font-medium text-foxy-text-secondary sm:pl-6"
                data-quote-page-metadata>
                Сторінка {{ payload.page_number }}
            </p>
            <section v-if="works.length" class="mt-4 border-t border-foxy-border/70 pt-3"
                aria-label="Джерело цитати" data-quote-source-context>
                <div class="flex flex-wrap gap-2">
                    <a v-for="work in works" :key="work.id" :href="work.url"
                        class="group inline-flex max-w-full items-center gap-2 rounded-lg border border-foxy-border/70 bg-foxy-elevated/50 px-2.5 py-2 transition-colors hover:border-foxy-action/50 hover:bg-foxy-elevated focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                        data-quote-source-link>
                        <img :src="work.cover" :alt="work.title" loading="lazy"
                            class="h-10 w-7 shrink-0 rounded bg-foxy-surface object-cover">
                        <span class="min-w-0">
                            <span class="block truncate text-sm font-semibold text-foxy-text transition-colors group-hover:text-foxy-action">
                                {{ work.title }}
                            </span>
                            <span v-if="work.author" class="block truncate text-xs text-foxy-text-secondary">
                                {{ work.author }}
                            </span>
                        </span>
                    </a>
                </div>
            </section>
        </section>

        <section v-else-if="item.family === 'fact'">
            <p class="text-foxy-text break-words">{{ payload.text }}</p>
        </section>

        <section v-else-if="item.family === 'collection'">
            <a v-if="item.url" :href="item.url" data-feed-detail
                class="block text-xl font-bold text-foxy-text hover:text-foxy-action break-words">
                {{ payload.name }}
            </a>
            <h2 v-else class="text-xl font-bold text-foxy-text break-words">{{ payload.name }}</h2>
            <p v-if="payload.description_preview" class="mt-2 text-foxy-text break-words">
                {{ payload.description_preview }}
            </p>
            <p v-if="item.work_context" class="mt-2 text-sm text-foxy-text-secondary">
                Книг у добірці: {{ item.work_context.total_count }}
            </p>
        </section>

        <section v-if="works.length && !['review', 'quote'].includes(item.family)" class="mt-5" aria-label="Пов’язані книги">
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <a v-for="work in works" :key="work.id" :href="work.url"
                    class="flex min-w-0 items-center gap-3 rounded-xl border border-foxy-border bg-foxy-elevated/70 p-3 hover:border-foxy-action/50">
                    <img :src="work.cover" :alt="work.title" loading="lazy"
                        class="h-16 w-11 shrink-0 rounded object-cover bg-foxy-surface">
                    <span class="min-w-0">
                        <span class="block line-clamp-2 text-sm font-semibold text-foxy-text">{{ work.title }}</span>
                        <span v-if="work.author" class="mt-1 block truncate text-xs text-foxy-text-secondary">
                            {{ work.author }}
                        </span>
                    </span>
                </a>
            </div>
            <p v-if="overflowCount > 0" class="mt-2 text-sm font-medium text-foxy-text-secondary">
                +{{ formatCount(overflowCount, 'інша книга', 'інші книги', 'інших книг') }}
            </p>
        </section>

        <footer class="mt-5 flex flex-wrap items-center gap-2 border-t border-foxy-border pt-4">
            <button v-if="capabilities.can_like" type="button"
                class="foxy-btn-secondary !px-3 !py-2 !text-sm"
                :class="{ 'text-red-500 dark:text-red-400': engagement.viewer_liked }"
                :aria-pressed="engagement.viewer_liked ? 'true' : 'false'"
                :aria-label="`Вподобати допис. Зараз вподобань: ${engagement.likes_count || 0}`"
                :disabled="actionPending"
                @click="toggleLike">
                <i class="fas fa-heart" aria-hidden="true"></i>
                {{ engagement.likes_count || 0 }}
            </button>
            <span v-else class="inline-flex items-center gap-2 px-3 py-2 text-sm text-foxy-text-secondary">
                <i class="fas fa-heart" aria-hidden="true"></i>
                {{ engagement.likes_count || 0 }}
            </span>

            <button v-if="capabilities.can_favorite && engagement.viewer_favorited !== null" type="button"
                class="foxy-btn-secondary !px-3 !py-2 !text-sm"
                :aria-pressed="engagement.viewer_favorited ? 'true' : 'false'"
                :aria-label="engagement.viewer_favorited ? 'Прибрати зі збереженого' : 'Зберегти'"
                :title="engagement.viewer_favorited ? 'Прибрати зі збереженого' : 'Зберегти'"
                :disabled="actionPending"
                @click="toggleFavorite">
                <i :class="[engagement.viewer_favorited ? 'fas' : 'far', 'fa-bookmark']" aria-hidden="true"></i>
                {{ engagement.viewer_favorited ? 'Збережено' : 'Зберегти' }}
            </button>

            <button v-if="capabilities.can_save && engagement.viewer_saved !== null" type="button"
                class="foxy-btn-secondary !px-3 !py-2 !text-sm"
                :aria-pressed="engagement.viewer_saved ? 'true' : 'false'"
                :aria-busy="item.family === 'collection' ? (actionPending ? 'true' : 'false') : null"
                :aria-label="engagement.viewer_saved ? 'Прибрати зі збереженого' : 'Зберегти'"
                :title="engagement.viewer_saved ? 'Прибрати зі збереженого' : 'Зберегти'"
                :disabled="actionPending"
                @click="toggleSave">
                <i :class="[engagement.viewer_saved ? 'fas' : 'far', 'fa-bookmark']" aria-hidden="true"></i>
                {{ engagement.viewer_saved ? 'Збережено' : 'Зберегти' }}
            </button>

            <a v-if="capabilities.can_reply && item.url" :href="item.url"
                :data-feed-detail="['discussion', 'review'].includes(item.family) ? 'true' : null"
                class="foxy-btn-secondary !px-3 !py-2 !text-sm">
                <i class="fas fa-reply" aria-hidden="true"></i>
                Відповісти
            </a>
        </footer>
        <p v-if="actionError" class="mt-3 text-sm text-foxy-error" role="alert">
            Не вдалося виконати дію. Спробуйте ще раз.
        </p>
    </article>
</template>

<script>
import axios from 'axios';
import { formatCount } from '../utils/ukrainianPluralization';
import {
    acknowledgeReviewSpoiler,
    currentViewerId,
    reviewSpoilerAcknowledgedEvent,
    shouldProtectReviewSpoiler,
} from '../utils/reviewSpoiler';

const ReplyPreview = {
    props: {
        preview: { type: Object, required: true },
        label: { type: String, required: true },
    },
    methods: { formatCount: (...args) => formatCount(...args) },
    template: `
        <aside class="feed-response-preview mt-4 ml-3 rounded-r-lg border-l-2 border-foxy-action/35 bg-foxy-elevated/35 py-3 pl-4 pr-3"
            data-response-preview>
            <p class="mb-1 flex items-center gap-1.5 text-xs font-semibold text-foxy-text-secondary">
                <i class="fas fa-comment-dots" aria-hidden="true"></i>
                {{ label }}
            </p>
            <p class="break-words text-sm text-foxy-text">{{ preview.text }}</p>
            <p class="mt-2 text-xs text-foxy-text-secondary">
                {{ preview.owner ? preview.owner.name : 'Користувач, якого видалено' }}
                <span v-if="preview.likes_count"> · {{ formatCount(preview.likes_count, 'вподобання', 'вподобання', 'вподобань') }}</span>
            </p>
        </aside>
    `,
};

export default {
    name: 'FeedCard',
    components: { ReplyPreview },
    props: {
        item: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            spoilerRevealed: false,
            spoilerAcknowledgedHandler: null,
            bodyExpanded: false,
            bodyOverflowing: false,
            bodyMeasured: false,
            discussionExpanded: false,
            discussionTextOverflowing: false,
            discussionMeasured: false,
            bodyResizeFrame: null,
            actionPending: false,
            actionError: false,
            engagement: { ...this.item.engagement },
        };
    },
    watch: {
        item: {
            deep: true,
            handler(nextItem) {
                this.engagement = { ...nextItem.engagement };
                this.spoilerRevealed = false;
                this.bodyExpanded = false;
                this.bodyOverflowing = false;
                this.bodyMeasured = false;
                this.discussionExpanded = false;
                this.discussionTextOverflowing = false;
                this.discussionMeasured = false;
                if (typeof this.$nextTick === 'function') {
                    this.$nextTick(() => this.scheduleBodyOverflowMeasurement());
                }
            },
        },
    },
    computed: {
        payload() {
            return this.item.payload || {};
        },
        bodyPlainText() {
            return this.plainTextFromBodyHtml(this.payload.body_html);
        },
        bodyRegionId() {
            return `feed-review-body-${this.item.source_id}`;
        },
        discussionBodyRegionId() {
            return `feed-discussion-body-${this.item.source_id}`;
        },
        discussionImages() {
            if (!this.payload.body_html || typeof DOMParser !== 'function') return [];

            const parsed = new DOMParser().parseFromString(String(this.payload.body_html), 'text/html');
            return Array.from(parsed.body.querySelectorAll('img')).map(image => ({
                src: image.getAttribute('src') || '',
                alt: image.getAttribute('alt') || '',
            }));
        },
        discussionFirstImage() {
            return this.discussionImages.length ? this.discussionImages[0] : null;
        },
        discussionNeedsCompact() {
            return this.discussionImages.length > 1 || this.discussionTextOverflowing;
        },
        showRichDiscussionBody() {
            return this.discussionExpanded || (this.discussionMeasured && !this.discussionNeedsCompact);
        },
        showRichReviewBody() {
            return this.bodyExpanded || (this.bodyMeasured && !this.bodyOverflowing);
        },
        owner() {
            return this.item.owner || null;
        },
        ownerName() {
            return this.owner ? this.owner.name : 'Користувач, якого видалено';
        },
        capabilities() {
            return this.item.capabilities || {};
        },
        works() {
            return this.item.work_context
                ? this.item.work_context.representative_works || []
                : [];
        },
        overflowCount() {
            return this.item.work_context ? this.item.work_context.overflow_count || 0 : 0;
        },
        familyLabel() {
            return {
                discussion: 'Дискусія',
                review: this.reviewLabel,
                quote: 'Цитата',
                fact: 'Факт',
                collection: 'Добірка',
            }[this.item.family] || this.item.family;
        },
        reviewLabel() {
            return this.payload.review_type === 'opinion' ? 'Відгук' : 'Рецензія';
        },
        moodPresentation() {
            const moods = {
                positive: {
                    label: 'Позитивні враження',
                    classes: 'border-foxy-success/60 bg-foxy-success/15 text-foxy-text',
                    dotClass: 'bg-foxy-success',
                },
                mixed: {
                    label: 'Змішані враження',
                    classes: 'border-yellow-600/50 bg-yellow-100/80 text-yellow-900 dark:border-yellow-300/45 dark:bg-yellow-300/15 dark:text-yellow-100',
                    dotClass: 'bg-yellow-600 dark:bg-yellow-300',
                },
                negative: {
                    label: 'Негативні враження',
                    classes: 'border-foxy-error/60 bg-foxy-error/15 text-foxy-text',
                    dotClass: 'bg-foxy-error',
                },
            };

            return Object.prototype.hasOwnProperty.call(moods, this.payload.mood)
                ? moods[this.payload.mood]
                : null;
        },
        displayDate() {
            if (!this.item.activity_at) return null;
            const date = new Date(this.item.activity_at);
            if (Number.isNaN(date.getTime())) return null;

            const dateParts = new Intl.DateTimeFormat('uk-UA', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            }).formatToParts(date);
            const part = type => {
                const match = dateParts.find(candidate => candidate.type === type);

                return match ? match.value : '';
            };
            const time = new Intl.DateTimeFormat('uk-UA', {
                hour: '2-digit',
                minute: '2-digit',
                hourCycle: 'h23',
            }).format(date);

            return `${part('day')} ${part('month')} ${part('year')} · ${time}`;
        },
        workSlug() {
            return this.works.length ? this.works[0].slug : null;
        },
        spoilerProtected() {
            if (this.item.family !== 'review') return false;

            return shouldProtectReviewSpoiler({
                id: this.item.source_id,
                user_id: this.owner ? this.owner.id : null,
                status: 'active',
                contains_spoiler: Boolean(this.payload.contains_spoiler),
                viewer_has_interacted: Boolean(this.payload.viewer_has_interacted),
            }, currentViewerId(), this.spoilerRevealed);
        },
    },
    mounted() {
        this.spoilerAcknowledgedHandler = event => {
            if (String(event.detail && event.detail.reviewId) === String(this.item.source_id)) {
                this.spoilerRevealed = true;
                if (typeof this.$nextTick === 'function') {
                    this.$nextTick(() => this.scheduleBodyOverflowMeasurement());
                }
            }
        };
        window.addEventListener(reviewSpoilerAcknowledgedEvent(), this.spoilerAcknowledgedHandler);
        window.addEventListener('resize', this.scheduleBodyOverflowMeasurement);
        this.scheduleBodyOverflowMeasurement();
    },
    beforeDestroy() {
        this.removeComponentListeners();
    },
    beforeUnmount() {
        this.removeComponentListeners();
    },
    methods: {
        formatCount: (...args) => formatCount(...args),
        plainTextFromBodyHtml(value) {
            if (!value || typeof DOMParser !== 'function') return '';

            const parsed = new DOMParser().parseFromString(String(value), 'text/html');
            const chunks = [];
            const blockElements = new Set([
                'address', 'article', 'aside', 'blockquote', 'div', 'footer', 'header',
                'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'main', 'nav', 'ol',
                'p', 'section', 'table', 'tr', 'ul',
            ]);
            const ignoredElements = new Set(['script', 'style', 'template']);
            const appendBreak = () => {
                if (chunks.length && chunks[chunks.length - 1] !== '\n') chunks.push('\n');
            };
            const visit = node => {
                if (node.nodeType === 3) {
                    chunks.push(node.textContent || '');
                    return;
                }
                if (node.nodeType !== 1) return;

                const tag = String(node.nodeName || '').toLowerCase();
                if (ignoredElements.has(tag)) return;
                if (tag === 'br') {
                    appendBreak();
                    return;
                }
                const block = blockElements.has(tag);
                if (block) appendBreak();
                Array.from(node.childNodes || []).forEach(visit);
                if (block) appendBreak();
            };

            Array.from(parsed.body ? parsed.body.childNodes : []).forEach(visit);

            return chunks.join('')
                .replace(/\u00a0/gu, ' ')
                .replace(/[^\S\r\n]+/gu, ' ')
                .replace(/ *\r?\n */gu, '\n')
                .replace(/\n{3,}/gu, '\n\n')
                .trim();
        },
        measureBodyOverflow() {
            if (this.item.family !== 'review' || this.spoilerProtected) {
                this.bodyOverflowing = false;
                this.bodyMeasured = false;
                return false;
            }

            const probe = this.$refs.bodyOverflowProbe;
            if (!probe) return false;
            this.bodyOverflowing = probe.scrollHeight > probe.clientHeight + 1;
            this.bodyMeasured = true;
            if (!this.bodyOverflowing) this.bodyExpanded = false;

            return this.bodyOverflowing;
        },
        measureDiscussionOverflow() {
            if (this.item.family !== 'discussion') {
                this.discussionTextOverflowing = false;
                this.discussionMeasured = false;
                return false;
            }

            const probe = this.$refs.discussionOverflowProbe;
            if (!probe) return false;
            this.discussionTextOverflowing = probe.scrollHeight > probe.clientHeight + 1;
            this.discussionMeasured = true;
            if (!this.discussionNeedsCompact) this.discussionExpanded = false;

            return this.discussionNeedsCompact;
        },
        scheduleBodyOverflowMeasurement() {
            if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
                const reviewOverflows = this.measureBodyOverflow();
                const discussionOverflows = this.measureDiscussionOverflow();
                return reviewOverflows || discussionOverflows;
            }
            if (this.bodyResizeFrame !== null) window.cancelAnimationFrame(this.bodyResizeFrame);
            this.bodyResizeFrame = window.requestAnimationFrame(() => {
                this.bodyResizeFrame = null;
                this.measureBodyOverflow();
                this.measureDiscussionOverflow();
            });

            return true;
        },
        toggleBodyExpanded() {
            if (!this.bodyOverflowing || this.spoilerProtected) return;
            this.bodyExpanded = !this.bodyExpanded;
        },
        toggleDiscussionExpanded() {
            if (!this.discussionMeasured || !this.discussionNeedsCompact) return;
            this.discussionExpanded = !this.discussionExpanded;
        },
        removeSpoilerAcknowledgementListener() {
            if (!this.spoilerAcknowledgedHandler) return;
            window.removeEventListener(reviewSpoilerAcknowledgedEvent(), this.spoilerAcknowledgedHandler);
            this.spoilerAcknowledgedHandler = null;
        },
        removeComponentListeners() {
            this.removeSpoilerAcknowledgementListener();
            window.removeEventListener('resize', this.scheduleBodyOverflowMeasurement);
            if (this.bodyResizeFrame !== null) {
                window.cancelAnimationFrame(this.bodyResizeFrame);
                this.bodyResizeFrame = null;
            }
        },
        revealSpoiler() {
            acknowledgeReviewSpoiler(this.item.source_id);
            this.spoilerRevealed = true;
            if (typeof this.$nextTick === 'function' && typeof this.scheduleBodyOverflowMeasurement === 'function') {
                this.$nextTick(() => this.scheduleBodyOverflowMeasurement());
            }
        },
        profileUrl(username) {
            return `/account/u/${encodeURIComponent(username)}/overview`;
        },
        async toggleLike() {
            if (!this.capabilities.can_like) return;
            await this.performAction(this.likeEndpoint(), 'like');
        },
        async toggleFavorite() {
            if (!this.capabilities.can_favorite) return;
            await this.performAction(this.favoriteEndpoint(), 'favorite');
        },
        async toggleSave() {
            if (!this.capabilities.can_save) return;
            if (this.item.family === 'discussion') {
                const method = this.engagement.viewer_saved ? 'delete' : 'put';
                await this.performAction(this.discussionSavedEndpoint(), 'save', method);
                return;
            }
            await this.performAction(this.collectionActionEndpoint('save'), 'save');
        },
        likeEndpoint() {
            if (this.item.family === 'discussion') {
                return this.item.url ? `${this.item.url}/like` : null;
            }
            if (this.item.family === 'review') {
                return this.item.url ? `${this.item.url}/like` : null;
            }
            if (this.item.family === 'quote' && this.workSlug) {
                return `/books/${encodeURIComponent(this.workSlug)}/quotes/${this.item.source_id}/like`;
            }
            if (this.item.family === 'fact' && this.workSlug) {
                return `/books/${encodeURIComponent(this.workSlug)}/facts/${this.item.source_id}/like`;
            }
            if (this.item.family === 'collection') {
                return this.collectionActionEndpoint('like');
            }
            return null;
        },
        favoriteEndpoint() {
            if (this.item.family === 'review') {
                return this.item.url ? `${this.item.url}/favorite` : null;
            }
            if (this.item.family === 'quote' && this.workSlug) {
                return `/books/${encodeURIComponent(this.workSlug)}/quotes/${this.item.source_id}/favorite`;
            }
            return null;
        },
        collectionActionEndpoint(action) {
            const sourceSlug = typeof this.item.source_slug === 'string'
                ? this.item.source_slug.trim()
                : '';
            if (!sourceSlug) return null;

            return `/libraries/${encodeURIComponent(sourceSlug)}/${action}`;
        },
        discussionSavedEndpoint() {
            const sourceId = Number(this.item.source_id);
            if (!Number.isInteger(sourceId) || sourceId < 1) return null;

            return `/discussions/${sourceId}/saved`;
        },
        async performAction(endpoint, action, method = 'post') {
            if (!endpoint || this.actionPending) return;
            this.actionPending = true;
            this.actionError = false;
            try {
                const { data } = await axios[method](endpoint);
                if (action === 'like') {
                    this.engagement.viewer_liked = data.is_liked !== undefined ? data.is_liked : data.liked;
                    this.engagement.likes_count = data.likes_count;
                } else if (action === 'favorite') {
                    this.engagement.viewer_favorited = data.is_favorited;
                } else if (action === 'save') {
                    this.engagement.viewer_saved = data.saved !== undefined ? data.saved : data.is_saved;
                }
            } catch (error) {
                if (error && error.response && [401, 419].includes(error.response.status)) {
                    const destination = `${window.location.pathname}${window.location.search}`;
                    window.location.assign(`/login?redirect=${encodeURIComponent(destination)}`);
                    return;
                }
                this.actionError = true;
                this.$emit('action-error', error);
            } finally {
                this.actionPending = false;
            }
        },
    },
};
</script>

<style scoped>
.content-preview ::v-deep img {
    display: block;
    width: auto;
    max-width: 100%;
    height: auto;
    max-height: 16rem;
    margin-left: auto;
    margin-right: auto;
    object-fit: contain;
}

@media (min-width: 640px) {
    .content-preview ::v-deep img {
        max-height: 20rem;
    }
}

.feed-review-body-clamp {
    display: -webkit-box;
    max-height: 9.75em;
    overflow: hidden;
    line-height: 1.625;
    white-space: pre-line;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 6;
}

.feed-review-body-probe {
    position: absolute;
    top: 0;
    left: 0;
    visibility: hidden;
    width: 100%;
    pointer-events: none;
}

.feed-review-spoiler-preview {
    max-height: 9.75em;
    overflow: hidden;
    pointer-events: none;
    user-select: none;
    -webkit-mask-image: linear-gradient(to bottom, #000 58%, transparent 100%);
    mask-image: linear-gradient(to bottom, #000 58%, transparent 100%);
}

.feed-review-spoiler-preview-text {
    opacity: 0.48;
    filter: blur(6px);
    transform: translateZ(0);
}
</style>
